import { computed, inject, Injectable, resource, signal } from '@angular/core';
import { MdcInventoryRepository } from '../repositories/inventory.repository';
import { MdcInventory } from '../types/mdc-inventory';
import { CreateMdcInventoryItem } from '../types/inventory-item';

@Injectable({ providedIn: 'root' })
export class MdcInventoryService {
  private readonly repository = inject(MdcInventoryRepository);

  private readonly _inventory = signal<MdcInventory | null>(null);

  private readonly _inventoryItemsResource = resource({
    params: () => this._inventory() ?? undefined,
    loader: async ({ params }) => {
      return this.repository.loadItems(params.id);
    },
  });

  readonly inventory = this._inventory.asReadonly();

  readonly items = computed(() => {
    const hasValue = this._inventoryItemsResource.hasValue();

    if (hasValue) {
      return this._inventoryItemsResource.value();
    }

    return [];
  });

  async loadById(inventoryId: number): Promise<MdcInventory | null> {
    const existingInventory = await this.repository.loadById(inventoryId);

    if (existingInventory) {
      this._inventory.set(existingInventory ?? null);
    }

    return existingInventory;
  }

  async createOrOpenInventory(date: string, stockId: number): Promise<MdcInventory> {
    const existingInventory = await this.repository.loadInventory(date, stockId);

    let inventory: MdcInventory;

    if (existingInventory && !!existingInventory?.['id']) {
      inventory = existingInventory;
    } else {
      inventory = await this.repository.create(date, stockId);
    }

    this._inventory.set(inventory ?? null);
    return inventory;
  }

  async createOrUpdateItem(id: number, payload: CreateMdcInventoryItem): Promise<any> {
    const result = await this.repository.addItem(id, payload);
    const items = await this.repository.loadItems(id);
    this._inventoryItemsResource.set(items);
    return result;
  }

  async addPallet(id: number, palletId: number): Promise<any[]> {
    const result = await this.repository.addPallet(id, palletId);
    const items = await this.repository.loadItems(id);
    this._inventoryItemsResource.set(items);
    return result;
  }

  async deleteItemsOfProduct(productId: number | string): Promise<any> {
    const inventory = this.inventory();
    if (!inventory) return;
    const result = await this.repository.deleteItemsOfProduct(inventory.id, productId);
    const items = await this.repository.loadItems(inventory.id);
    this._inventoryItemsResource.set(items);
    return items;
  }

  async finalize(): Promise<boolean> {
    const inventory = this.inventory();
    if (!inventory) return false;
    await this.repository.finalize(inventory.id);
    this._inventory.set(null);
    this._inventoryItemsResource.set([]);
    return true;
  }

  async delete(): Promise<void> {
    const inventory = this.inventory();
    if (!inventory) return;
    this.repository.delete(inventory.id);
  }
}
